import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Send, ArrowLeft, Paperclip, Landmark, Star, X, Check, FileDown, Loader2, Link2 } from 'lucide-react';
import Shell from '../../components/TopBarShell';
import StatusBadge from '../../components/StatusBadge';
import OdooBadge from '../../components/OdooBadge';
import FileDropZone from '../../components/FileDropZone';
import { Button } from '../../components/ui/button';
import { useReviewQueue } from '../../contexts/ReviewQueueContext';
import { useAuth } from '../../contexts/AuthProvider';
import { useLocale, useT } from '../../contexts/LocaleProvider';
import { deptLabel } from '../../lib/departments';
import api from '../../utils/api';

const TYPES = ['restaurant', 'logement', 'transport', 'autres'];

const emptyLine = () => ({
  date: new Date().toISOString().slice(0, 10),
  type: 'restaurant',
  description: '',
  participants: '',
  total_htva: '0.00',
  total_tvac: '0.00',
});

const fmt = (n) => Number(n || 0).toFixed(2);

/* VAT % derived from what the user typed (TVAC vs HTVA). Empty when
 * either value is missing or HTVA is zero · we don't want a phantom
 * "100%" on a free line. Chip styling lifts a recognised rate (6, 12,
 * 21) so admins can spot a typo (e.g. 1,2 → 100% TVA). */
const VAT_NORMAL = new Set([6, 12, 21]);
const VatChip = ({ htva, tvac, muted = false }) => {
  const h = Number(htva || 0);
  const c = Number(tvac || 0);
  if (!h || !c || c < h) {
    return <span className="text-[11.5px] text-[hsl(var(--text-muted))]">—</span>;
  }
  const pctRaw = (c - h) / h * 100;
  const rounded = Math.round(pctRaw);
  const isRecognised = VAT_NORMAL.has(rounded) && Math.abs(pctRaw - rounded) < 0.5;
  const tone = muted
    ? 'text-[hsl(var(--text-soft))]'
    : isRecognised
      ? 'text-[hsl(var(--accent))]'
      : 'text-[hsl(var(--warning))]';
  return (
    <span className={`font-num text-[12px] ${tone}`} title={`${pctRaw.toFixed(2)} %`}>
      {pctRaw % 1 < 0.05 || pctRaw % 1 > 0.95 ? rounded : pctRaw.toFixed(1)} %
    </span>
  );
};
const fmtIban = (s) => {
  const compact = (s || '').replace(/\s+/g, '').toUpperCase();
  return compact.match(/.{1,4}/g)?.join(' ') || '';
};

const ExpenseEditor = () => {
  const { id } = useParams();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const { count: reviewCount } = useReviewQueue();
  const t = useT();
  const { user } = useAuth();
  const { locale, intlLocale } = useLocale();
  const fmtDate = (iso) => iso ? new Intl.DateTimeFormat(intlLocale).format(new Date(iso)) : '·';

  const [employee, setEmployee] = useState(null);
  const [report, setReport] = useState(null);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);  // distinct from generic saving · drives the submit-specific feedback
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ iban: '', label: '' });
  const [accountError, setAccountError] = useState('');
  const [workflow, setWorkflow] = useState(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  useEffect(() => {
    api.myEmployee().then(setEmployee).catch(() => setEmployee(null));
    api.myBankAccounts().then(arr => {
      setAccounts(arr);
      const pref = arr.find(a => a.is_preferred) || arr[0];
      if (pref) setSelectedAccountId(pref.id);
    }).catch(() => setAccounts([]));
    api.expenseWorkflow().then(setWorkflow).catch(() => setWorkflow(null));
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.expense(id).then((r) => {
      setReport(r);
      setTitle(r.title || '');
      setNote(r.note || '');
      setLines(r.lines.length ? r.lines.map(l => ({ ...l, total_htva: fmt(l.total_htva), total_tvac: fmt(l.total_tvac) })) : [emptyLine()]);
      setAttachments(r.attachments);
    }).catch(() => setError(t('editor.notFound')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const totals = useMemo(() => ({
    htva: lines.reduce((s, l) => s + Number(l.total_htva || 0), 0),
    tvac: lines.reduce((s, l) => s + Number(l.total_tvac || 0), 0),
  }), [lines]);

  // Editable until the manager approves. Submitted + rejected stay open.
  const isEditable = !report || report.status !== 'approved';
  const canSubmit  = !report || report.status === 'draft' || report.status === 'rejected';
  const submitLabel = report?.status === 'rejected' ? t('editor.resend') : t('editor.send');

  const setLine = (i, patch) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, emptyLine()]);
  const delLine = (i) => setLines(ls => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        title, note,
        // Round-trip the existing line id so the backend can match and
        // update in place · without it, the backend would create a new
        // row and drop the attachment.line FK that points at it.
        lines: lines.map(l => ({
          ...(l.id ? { id: l.id } : {}),
          date: l.date, type: l.type, description: l.description, participants: l.participants,
          total_htva: Number(l.total_htva || 0),
          total_tvac: Number(l.total_tvac || 0),
        })),
      };
      const r = report ? await api.updateExpense(report.id, payload) : await api.createExpense(payload);
      setReport(r);
      // Sync local lines with the freshly persisted ones so any newly
      // assigned IDs (for previously unsaved rows) replace the in-memory
      // placeholders. Keeps the position stable.
      setLines(r.lines.length
        ? r.lines.map(l => ({ ...l, total_htva: fmt(l.total_htva), total_tvac: fmt(l.total_tvac) }))
        : [emptyLine()]);
      setAttachments(r.attachments);
      if (isNew) navigate(`/expenses/${r.id}`, { replace: true });
      return r;
    } catch (e) {
      setError(e.response?.data?.detail || t('editor.saveFailed'));
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    setSubmitting(true);
    setError('');
    try {
      const saved = await save();
      const r = await api.submitExpense(saved?.id || report?.id || id, locale, selectedAccountId);
      setReport(r);
      setJustSubmitted(true);
    } catch (e) {
      setError(e.response?.data?.detail || t('editor.submitFailed'));
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const saveAccount = async () => {
    setAccountError('');
    const iban = newAccount.iban.trim();
    if (!iban) { setAccountError(t('bank.ibanRequired')); return; }
    try {
      const a = await api.createBankAccount({ iban, label: newAccount.label.trim() });
      setAccounts(arr => [...arr.filter(x => x.id !== a.id), a]);
      setSelectedAccountId(a.id);
      setNewAccount({ iban: '', label: '' });
      setAddingAccount(false);
    } catch (e) {
      const d = e.response?.data;
      setAccountError(Array.isArray(d?.iban) ? d.iban[0] : (d?.detail || t('bank.saveFailed')));
    }
  };
  const makePreferred = async (a) => {
    try {
      const updated = await api.updateBankAccount(a.id, { is_preferred: true });
      setAccounts(arr => arr.map(x => x.id === updated.id
        ? updated
        : { ...x, is_preferred: false }));
    } catch (e) {
      setAccountError(e.response?.data?.detail || t('bank.saveFailed'));
    }
  };
  const removeAccount = async (a) => {
    if (!window.confirm(t('bank.confirmDelete', { iban: fmtIban(a.iban) }))) return;
    try {
      await api.deleteBankAccount(a.id);
      const next = accounts.filter(x => x.id !== a.id);
      setAccounts(next);
      if (selectedAccountId === a.id) {
        const newPref = next.find(x => x.is_preferred) || next[0];
        setSelectedAccountId(newPref?.id || null);
      }
    } catch (e) {
      setAccountError(e.response?.data?.detail || t('bank.saveFailed'));
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadCombinedPdf = async () => {
    if (!report) return;
    setDownloadingPdf(true);
    setError('');
    try {
      const blob = await api.combinedPdf(report.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NDF-${String(report.id).padStart(5, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(t('editor.pdfFailed'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Count receipts per line for the inline indicator on each line row.
  // Keyed by line.id · new lines have no id yet, so they always show 0.
  const receiptsByLine = useMemo(() => {
    const m = new Map();
    for (const a of attachments) {
      if (a.line) m.set(a.line, (m.get(a.line) || 0) + 1);
    }
    return m;
  }, [attachments]);

  // Build dropdown options from local `lines` (so labels reflect what the
  // user sees) but only for rows that already have a server id · the FK
  // needs something real on the backend. Unsaved new rows are skipped;
  // the visible position uses the original index so numbering matches
  // the line table even when mixed with unsaved rows.
  // Every line is selectable in the attachment-link dropdown, even
  // before it has a server id · unsaved rows get a sentinel value and
  // are materialized by an auto-save when the user picks one.
  const linkableLines = useMemo(
    () => lines.map((l, i) => ({
      key: l.id ? String(l.id) : `__unsaved:${i}`,
      isUnsaved: !l.id,
      index: i,
      label: t('editor.attach.lineOption', {
        n: i + 1,
        date: l.date ? new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: '2-digit' }).format(new Date(l.date)) : '·',
        desc: l.description?.trim() || t(`expense.type.${l.type}`),
      }),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, intlLocale, locale],
  );
  const hasUnsavedLines = lines.some(l => !l.id);

  const setAttachmentLine = async (att, rawValue) => {
    if (!report) return;
    let nextLineId = null;
    if (rawValue && typeof rawValue === 'string' && rawValue.startsWith('__unsaved:')) {
      // Save the report first so the new line gets a real PK · without
      // this, the backend can't attach the file to anything.
      const idx = Number(rawValue.slice('__unsaved:'.length));
      try {
        const r = await save();
        nextLineId = r.lines?.[idx]?.id || null;
      } catch {
        return;  // error already surfaced by save()
      }
    } else if (rawValue) {
      nextLineId = Number(rawValue);
    }
    // Optimistic · the PATCH is tiny and the editor needs to feel instant.
    setAttachments(arr => arr.map(a => a.id === att.id ? { ...a, line: nextLineId } : a));
    try {
      await api.updateAttachment(report.id, att.id, { line: nextLineId });
    } catch (err) {
      setAttachments(arr => arr.map(a => a.id === att.id ? { ...a, line: att.line || null } : a));
      setError(err.response?.data?.detail || t('editor.saveFailed'));
    }
  };

  const deleteAttachment = async (att) => {
    if (!report) return;
    if (!window.confirm(t('editor.attach.confirmDelete'))) return;
    try {
      await api.deleteAttachment(report.id, att.id);
      setAttachments(arr => arr.filter(a => a.id !== att.id));
    } catch (err) {
      setError(err.response?.data?.detail || t('editor.saveFailed'));
    }
  };

  const [uploading, setUploading] = useState(false);
  const handleFiles = async (files) => {
    if (!report) {
      setError(t('editor.saveBeforeUpload'));
      return;
    }
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      const uploaded = [];
      for (const f of files) {
        const att = await api.uploadAttachment(report.id, f, 'receipt');
        uploaded.push(att);
      }
      setAttachments(a => [...a, ...uploaded]);
    } catch (err) {
      const detail = err.response?.data?.detail
        || (typeof err.response?.data === 'string' ? err.response.data : null)
        || err.message;
      setError(`${t('editor.uploadFailed')} : ${detail || ''}`);
      console.error('[upload]', err.response || err);
    } finally {
      setUploading(false);
    }
  };

  const managerLabel = employee?.manager?.full_name || employee?.manager_name || report?.submitter_manager_name || '·';
  const managerMissing = !employee?.manager?.full_name && !employee?.manager_name && !report?.submitter_manager_name;

  return (
    <Shell reviewCount={reviewCount}>
      <button onClick={() => navigate('/expenses')} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))] mb-3">
        <ArrowLeft className="h-4 w-4" /> {t('editor.backToList')}
      </button>

      <header className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[13px] text-[hsl(var(--text-muted))] font-num">
            {report ? `NDF-${String(report.id).padStart(5, '0')}` : t('editor.newNote')}
          </div>
          <h1 className="font-display text-[1.75rem] mt-1">
            {isNew ? t('editor.newNote') : (report?.title || t('editor.note'))}
          </h1>
        </div>
        {report && <OdooBadge odoo={report.odoo} />}
        {report && <StatusBadge status={report.status} />}
      </header>

      {error && (
        <div className="mb-4 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
          {error}
        </div>
      )}

      {/* Header form */}
      <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Static label={t('editor.collaborator')}  value={employee?.full_name || '·'} />
          <Static label={t('editor.department')}    value={employee?.departement ? deptLabel(employee.departement, locale) : '·'} />
          <div>
            <Static label={t('editor.manager')}       value={managerLabel} />
            {managerMissing && (
              <p className="text-[11px] text-[hsl(var(--accent))] mt-0.5">
                {t('editor.manager.notSet')}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="field">
            <label className="field-label">{t('editor.titleField')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span></label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!isEditable}
              placeholder={t('editor.title.ph')} />
          </div>
          <div className="field">
            <label className="field-label">{t('editor.accountingNote')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span></label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} disabled={!isEditable}
              placeholder={t('editor.accounting.ph')} />
          </div>
        </div>
      </section>

      {/* Lines */}
      <section className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden mb-5">
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold">{t('editor.lines')}</h2>
            <p className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">
              {t('editor.lines.dateHint')}
            </p>
          </div>
          {isEditable && (
            <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5 shrink-0">
              <Plus className="h-3.5 w-3.5" /> {t('common.add')}
            </Button>
          )}
        </div>

        <table className="w-full text-[15px]">
          <thead className="bg-[hsl(var(--bg-subtle))] text-[13px] text-[hsl(var(--text-muted))]">
            <tr>
              <th className="text-left font-medium px-4 py-3 w-[150px]">{t('expense.col.date')}</th>
              <th className="text-left font-medium px-4 py-3 w-[150px]">{t('editor.col.type')}</th>
              <th className="text-left font-medium px-4 py-3">{t('editor.col.description')}</th>
              <th className="text-left font-medium px-4 py-3">{t('editor.col.participants')}</th>
              <th className="text-right font-medium px-4 py-3 w-[120px]">{t('editor.col.htva')}</th>
              <th className="text-right font-medium px-4 py-3 w-[60px]">{t('editor.col.vat') || 'TVA'}</th>
              <th className="text-right font-medium px-4 py-3 w-[120px]">{t('editor.col.tvac')}</th>
              <th className="w-[40px]" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              // `align-top` keeps every input pinned to the top of the
              // row · without it, a row with the "2 bewijs" indicator
              // grew taller and pushed its date / amount inputs below
              // the inputs in rows without attachments.
              <tr key={i} className="border-t border-[hsl(var(--rule))] align-top">
                <td className="px-3 py-2">
                  <input type="date" className="input" value={l.date} disabled={!isEditable}
                    onChange={(e) => setLine(i, { date: e.target.value })} />
                </td>
                <td className="px-3 py-2">
                  <select className="input" value={l.type} disabled={!isEditable}
                    onChange={(e) => setLine(i, { type: e.target.value })}>
                    {TYPES.map(v => <option key={v} value={v}>{t(`expense.type.${v}`)}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input type="text" className="input" value={l.description} disabled={!isEditable}
                    onChange={(e) => setLine(i, { description: e.target.value })}
                    placeholder={t('expense.desc.ph')} />
                  {l.id && (() => {
                    const n = receiptsByLine.get(l.id) || 0;
                    const text = n > 0 ? t('editor.line.receipts', { n }) : t('editor.line.noReceipt');
                    return (
                      <div className={`mt-1 inline-flex items-center gap-1 text-[11px] ${n > 0 ? 'text-[hsl(var(--text-soft))]' : 'text-[hsl(var(--text-muted))]'}`}>
                        <Paperclip className="h-3 w-3" /> {text}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-3 py-2">
                  <input type="text" className="input" value={l.participants} disabled={!isEditable}
                    onChange={(e) => setLine(i, { participants: e.target.value })}
                    placeholder={t('expense.participants.ph')} />
                </td>
                <td className="px-3 py-2">
                  <input type="number" step="0.01" className="input font-num text-right" value={l.total_htva} disabled={!isEditable}
                    onChange={(e) => setLine(i, { total_htva: e.target.value })} />
                </td>
                <td className="px-3 py-3 text-right">
                  <VatChip htva={l.total_htva} tvac={l.total_tvac} />
                </td>
                <td className="px-3 py-2">
                  <input type="number" step="0.01" className="input font-num text-right" value={l.total_tvac} disabled={!isEditable}
                    onChange={(e) => setLine(i, { total_tvac: e.target.value })} />
                </td>
                <td className="px-3 py-3 text-right">
                  {isEditable && lines.length > 1 && (
                    <button type="button" onClick={() => delLine(i)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label={t('common.delete')}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-[hsl(var(--bg-subtle))]">
              <td colSpan={4} className="px-4 py-3.5 text-[15px] font-semibold">{t('editor.totals')}</td>
              <td className="px-4 py-3.5 text-right font-num">€ {totals.htva.toFixed(2)}</td>
              <td className="px-3 py-3.5 text-right">
                <VatChip htva={totals.htva} tvac={totals.tvac} muted />
              </td>
              <td className="px-4 py-3.5 text-right font-num font-semibold">€ {totals.tvac.toFixed(2)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </section>

      {/* Bank account */}
      <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-3 flex items-center gap-2">
          <Landmark className="h-4 w-4 text-[hsl(var(--text-muted))]" />
          {t('editor.bank')}
        </h2>

        {accountError && (
          <div className="mb-3 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-sm rounded-md">
            {accountError}
          </div>
        )}

        {accounts.length === 0 && !addingAccount && (
          <p className="text-sm text-[hsl(var(--text-soft))] mb-3">{t('bank.empty')}</p>
        )}

        {accounts.length > 0 && (
          <ul className="space-y-1.5 mb-3">
            {accounts.map(a => {
              const checked = selectedAccountId === a.id;
              return (
                <li key={a.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md border ${
                    checked
                      ? 'border-[hsl(var(--text))] bg-[hsl(var(--bg-subtle))]'
                      : 'border-border bg-[hsl(var(--surface))]'
                  }`}>
                  <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                    <input type="radio" name="bank-account" checked={checked}
                      onChange={() => setSelectedAccountId(a.id)}
                      disabled={!isEditable} />
                    <div className="min-w-0">
                      <div className="font-num text-[14px]">{fmtIban(a.iban)}</div>
                      {a.label && <div className="text-[12px] text-[hsl(var(--text-muted))] truncate">{a.label}</div>}
                    </div>
                  </label>
                  {a.is_preferred ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--accent))] font-medium">
                      <Star className="h-3 w-3 fill-current" /> {t('bank.preferred')}
                    </span>
                  ) : (
                    isEditable && (
                      <button type="button" onClick={() => makePreferred(a)}
                        className="text-[11px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">
                        {t('bank.makePreferred')}
                      </button>
                    )
                  )}
                  {isEditable && (
                    <button type="button" onClick={() => removeAccount(a)}
                      className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label={t('common.delete')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {addingAccount && (
          <div className="border border-border rounded-md p-3 mb-3 bg-[hsl(var(--bg-subtle))] space-y-2">
            <div className="field">
              <label className="field-label">{t('bank.iban')} *</label>
              <input className="input font-num" value={newAccount.iban}
                onChange={e => setNewAccount({ ...newAccount, iban: e.target.value })}
                placeholder="BE71 1234 5678 9012" />
            </div>
            <div className="field">
              <label className="field-label">
                {t('bank.label')} <span className="text-[hsl(var(--text-muted))]">{t('common.optional')}</span>
              </label>
              <input className="input" value={newAccount.label}
                onChange={e => setNewAccount({ ...newAccount, label: e.target.value })}
                placeholder={t('bank.label.ph')} />
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" className="gap-1.5"
                onClick={() => { setAddingAccount(false); setNewAccount({ iban: '', label: '' }); setAccountError(''); }}>
                <X className="h-3.5 w-3.5" /> {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={saveAccount} className="gap-1.5">
                <Check className="h-3.5 w-3.5" /> {t('common.save')}
              </Button>
            </div>
          </div>
        )}

        {isEditable && !addingAccount && (
          <button type="button" onClick={() => setAddingAccount(true)}
            className="inline-flex items-center gap-1.5 text-[13px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">
            <Plus className="h-3.5 w-3.5" /> {t('bank.add')}
          </button>
        )}
      </section>

      {/* Attachments */}
      <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-3">{t('editor.attachments')}</h2>

        {!report && (
          <div className="rounded-md border border-dashed border-[hsl(var(--accent)/.4)] bg-[hsl(var(--accent-soft))] px-4 py-3 flex items-center gap-3 flex-wrap">
            <Paperclip className="h-4 w-4 text-[hsl(var(--accent))] shrink-0" />
            <p className="text-sm text-[hsl(var(--text))] flex-1 min-w-[200px]">
              {t('editor.saveBeforeAttach')}
            </p>
            <Button size="sm" onClick={save} disabled={saving} className="gap-1.5">
              {saving ? t('editor.saving') : t('editor.saveDraftNow')}
            </Button>
          </div>
        )}

        {report && (
          <>
            {attachments.length > 0 && (
              <>
                <ul className="space-y-1.5 mb-3">
                  {attachments.map(a => (
                    <li key={a.id} className="flex items-center gap-3 px-3 py-2 border border-border rounded-md bg-[hsl(var(--bg-subtle))] text-sm">
                      <a href={a.file} target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 text-[hsl(var(--accent))] hover:underline truncate flex-1 min-w-0">
                        <Paperclip className="h-4 w-4 shrink-0" />
                        <span className="truncate">{a.original_name || a.file.split('/').pop()}</span>
                      </a>
                      {linkableLines.length > 0 && (
                        <label className="flex items-center gap-1.5 text-[12px] text-[hsl(var(--text-muted))] shrink-0">
                          <Link2 className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{t('editor.attach.line')}</span>
                          <select
                            className="input py-1 text-[12px] max-w-[260px]"
                            value={a.line ? String(a.line) : ''}
                            disabled={!isEditable}
                            onChange={(e) => setAttachmentLine(a, e.target.value || null)}
                          >
                            <option value="">{t('editor.attach.line.none')}</option>
                            {linkableLines.map(opt => (
                              <option key={opt.key} value={opt.key}>{opt.label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                      {isEditable && (
                        <button type="button" onClick={() => deleteAttachment(a)}
                          className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))] shrink-0"
                          aria-label={t('editor.attach.delete')}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {isEditable && linkableLines.length === 0 && hasUnsavedLines && (
                  <p className="text-[12px] text-[hsl(var(--text-muted))] italic mb-3">
                    {t('editor.attach.lineHelp')}
                  </p>
                )}
                <div className="mb-3">
                  <Button variant="outline" size="sm" onClick={downloadCombinedPdf}
                    disabled={downloadingPdf} className="gap-1.5">
                    <FileDown className="h-3.5 w-3.5" />
                    {downloadingPdf ? t('editor.pdfBuilding') : t('editor.downloadCombined')}
                  </Button>
                </div>
              </>
            )}

            {isEditable && (
              <FileDropZone
                onFiles={handleFiles}
                disabled={uploading}
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                busyLabel={uploading ? t('editor.uploadingShort') : null}
                label={t('editor.attach.drop')}
                sublabel={t('editor.attach.sub')}
              />
            )}
          </>
        )}
      </section>

      {/* Manager decision (read-only, when present) */}
      {report && (report.status === 'submitted' || report.status === 'approved' || report.status === 'rejected') && (() => {
        const myEmail = (user?.email || '').toLowerCase();
        const iAmTheManager = report.status === 'submitted'
          && (report.submitter_manager_email || '').toLowerCase() === myEmail
          && report.submitter_email?.toLowerCase() !== myEmail;
        return (
        <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
          <h2 className="text-[15px] font-semibold mb-3">{t('editor.validation')}</h2>
          {iAmTheManager && (
            <div className="mb-3 rounded-md border border-[hsl(var(--accent)/.4)] bg-[hsl(var(--accent-soft))] px-4 py-3 flex items-center gap-3 flex-wrap">
              <Check className="h-4 w-4 text-[hsl(var(--accent))] shrink-0" />
              <p className="text-sm flex-1 min-w-[200px]">
                {t('editor.iAmTheManager')}
              </p>
              <Button size="sm" onClick={() => navigate(`/review/${report.id}`)} className="gap-1.5">
                {t('editor.openReview')}
              </Button>
            </div>
          )}
          {report.status === 'submitted' && (
            <WorkflowSteps
              t={t}
              status="submitted"
              managerName={report.submitter_manager_name || workflow?.manager?.name || managerLabel}
              managerEmail={report.submitter_manager_email || workflow?.manager?.email}
              accountingName={workflow?.accounting?.name}
              accountingEmail={workflow?.accounting?.email}
            />
          )}
          {report.status !== 'submitted' && (
            <div className="space-y-2 text-sm">
              <div className="text-[hsl(var(--text-soft))]">
                {t('editor.decidedBy', {
                  decision: report.status === 'approved' ? t('status.approved') : t('status.rejected'),
                  who: report.manager_decided_by_email || '·',
                  date: fmtDate(report.manager_decided_at),
                })}
              </div>
              {report.manager_comment && (
                <div className="border-l-2 border-[hsl(var(--rule-strong))] pl-3 py-1 text-[hsl(var(--text))]">
                  {report.manager_comment}
                </div>
              )}
            </div>
          )}
        </section>
        );
      })()}

      {isEditable && (() => {
        const needsAttachment = attachments.length === 0;
        const needsAccount = !selectedAccountId;
        const blockMsg = needsAccount
          ? t('editor.accountRequired')
          : (needsAttachment ? t('editor.attachRequired') : null);
        return (
          <div className="flex flex-col items-end gap-1.5 mt-6">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={save} disabled={saving}>
                {saving ? t('editor.saving') : (report?.status === 'submitted' ? t('editor.save') : t('editor.saveDraft'))}
              </Button>
              {canSubmit && (
                <Button onClick={submit} disabled={saving || needsAttachment || needsAccount} className="gap-2"
                  title={blockMsg || undefined}>
                  {submitting
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Send className="h-4 w-4" />}
                  {submitting ? t('editor.submitting') : submitLabel}
                </Button>
              )}
            </div>
            {canSubmit && blockMsg && (
              <span className="text-[12px] text-[hsl(var(--text-muted))]">{blockMsg}</span>
            )}
          </div>
        );
      })()}

      {submitting && (
        <SubmittingOverlay t={t} />
      )}

      {justSubmitted && (
        <SubmitSuccessModal
          t={t}
          managerName={report?.submitter_manager_name || workflow?.manager?.name || managerLabel}
          managerEmail={report?.submitter_manager_email || workflow?.manager?.email}
          accountingName={workflow?.accounting?.name}
          accountingEmail={workflow?.accounting?.email}
          onClose={() => setJustSubmitted(false)}
        />
      )}
    </Shell>
  );
};

/* Full-screen "we're working on it" overlay shown the instant the user
 * clicks submit · the network round-trip + email send can take a few
 * seconds, and a silent button was reading as a bug. */
const SubmittingOverlay = ({ t }) => (
  <div className="fixed inset-0 z-40 flex items-center justify-center"
       style={{ background: 'rgba(20, 30, 25, 0.45)' }}
       aria-live="polite" aria-busy="true">
    <div className="rounded-2xl bg-[hsl(var(--surface))] border border-border px-6 py-5 shadow-xl flex items-center gap-3">
      <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--accent))]" />
      <div className="leading-tight">
        <div className="text-[14px] font-medium">{t('editor.submitting.title')}</div>
        <div className="text-[12px] text-[hsl(var(--text-soft))]">{t('editor.submitting.lede')}</div>
      </div>
    </div>
  </div>
);

/* What happens next · shown inline once a report is submitted and as a
 * modal on the success transition. Pulls real names from the workflow
 * endpoint so the user sees "Robbie Nevens" rather than a placeholder. */
const WorkflowSteps = ({ t, status, managerName, managerEmail, accountingName, accountingEmail }) => (
  <ol className="space-y-2.5 text-[14px]">
    <WorkflowStep
      done
      title={t('editor.flow.step1.title')}
      body={t('editor.flow.step1.body', {
        name: managerName || t('editor.flow.yourManager'),
        email: managerEmail || '',
      })}
    />
    <WorkflowStep
      done={status === 'approved' || status === 'rejected'}
      title={t('editor.flow.step2.title', { name: managerName || t('editor.flow.yourManager') })}
      body={t('editor.flow.step2.body')}
    />
    <WorkflowStep
      title={t('editor.flow.step3.title')}
      body={
        accountingName
          ? t('editor.flow.step3.body', { name: accountingName, email: accountingEmail || '' })
          : t('editor.flow.step3.bodyGeneric')
      }
    />
    <WorkflowStep
      title={t('editor.flow.step4.title')}
      body={t('editor.flow.step4.body')}
    />
  </ol>
);

const WorkflowStep = ({ done, title, body }) => (
  <li className="flex items-start gap-3">
    <span
      aria-hidden
      className={`mt-1 h-2 w-2 rounded-full shrink-0 ${done ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--rule-strong))]'}`}
    />
    <div className="min-w-0">
      <div className="font-medium leading-tight">{title}</div>
      <div className="text-[13px] text-[hsl(var(--text-soft))] mt-0.5 leading-snug">{body}</div>
    </div>
  </li>
);

const SubmitSuccessModal = ({ t, managerName, managerEmail, accountingName, accountingEmail, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: 'rgba(20, 30, 25, 0.5)' }}
    onClick={onClose}
  >
    <div
      className="relative w-full max-w-[520px] rounded-2xl bg-[hsl(var(--surface))] border border-border p-6 shadow-xl"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[hsl(var(--surface-2))]"
        aria-label={t('common.close') || 'Close'}
      >
        <X className="h-4 w-4 text-[hsl(var(--text-muted))]" />
      </button>

      <div className="flex items-center gap-3 mb-2">
        <span className="h-9 w-9 rounded-full bg-[hsl(var(--accent-soft))] flex items-center justify-center">
          <Check className="h-4 w-4 text-[hsl(var(--accent))]" />
        </span>
        <h3 className="font-display text-[1.25rem] leading-tight">
          {t('editor.submitSuccess.title')}
        </h3>
      </div>
      <p className="text-[14px] text-[hsl(var(--text-soft))] mb-4">
        {t('editor.submitSuccess.lede', {
          name: managerName || t('editor.flow.yourManager'),
        })}
      </p>

      <div className="rounded-lg bg-[hsl(var(--bg-subtle))] border border-border p-4 mb-4">
        <WorkflowSteps
          t={t}
          status="submitted"
          managerName={managerName}
          managerEmail={managerEmail}
          accountingName={accountingName}
          accountingEmail={accountingEmail}
        />
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose} className="gap-1.5">
          <Check className="h-3.5 w-3.5" /> {t('editor.submitSuccess.ack')}
        </Button>
      </div>
    </div>
  </div>
);

const Static = ({ label, value }) => (
  <div>
    <div className="text-[13px] text-[hsl(var(--text-muted))]">{label}</div>
    <div className="text-[15px] mt-1">{value}</div>
  </div>
);

export default ExpenseEditor;
